/**
 * Lecture de la sonde SDI exposée par le playout (`casparStatus.liveSignal`).
 *
 * Miroir frontend de `backend/src/lib/live-signal-probe.ts`. Purement
 * informatif : rien dans la régie ne décide à partir de cette valeur, elle
 * sert à prévenir l'opérateur AVANT la prise d'antenne qu'une image noire
 * l'attend. `INFO TIME` restant à zéro sur une entrée DeckLink, c'est la seule
 * mesure explicite disponible.
 */

export type LiveSignalState = 'SIGNAL_PRESENT' | 'NO_SIGNAL' | 'NOT_DECKLINK' | 'UNKNOWN'

const KNOWN: readonly LiveSignalState[] = ['SIGNAL_PRESENT', 'NO_SIGNAL', 'NOT_DECKLINK', 'UNKNOWN']

/**
 * Extrait l'état du signal depuis `casparStatus`.
 *
 * Toute valeur absente ou inattendue retombe sur `UNKNOWN` : un playout plus
 * ancien, qui n'émet pas encore ce champ, ne doit jamais faire croire à une
 * perte de signal.
 */
export function readLiveSignalState(casparStatus: Record<string, unknown> | null | undefined): LiveSignalState {
  const raw = casparStatus?.liveSignal
  return typeof raw === 'string' && (KNOWN as readonly string[]).includes(raw)
    ? raw as LiveSignalState
    : 'UNKNOWN'
}

/**
 * Vrai uniquement quand l'absence de signal est CERTAINE.
 *
 * `UNKNOWN` et `NOT_DECKLINK` ne doivent jamais allumer l'alerte : afficher
 * « pas de signal » sur une incertitude enverrait l'opérateur chercher une
 * panne qui n'existe pas, exactement le défaut des faux positifs historiques.
 */
export function hasNoSdiSignal(casparStatus: Record<string, unknown> | null | undefined): boolean {
  return readLiveSignalState(casparStatus) === 'NO_SIGNAL'
}

export const LIVE_SIGNAL_LABEL: Record<LiveSignalState, string> = {
  SIGNAL_PRESENT: 'Signal SDI présent',
  NO_SIGNAL:      'Aucun signal SDI',
  NOT_DECKLINK:   'Pas d’entrée SDI active',
  UNKNOWN:        'Signal SDI indéterminé',
}
